/* Print A Bill — custom denomination.
 *
 * Deliberately implemented outside pab-mockup.js. That file is a single
 * 30 KB line and already contains a complete `$custom` code path — the bill
 * label, the preview selection label, setCustomPreview() and callout
 * suppression all branch on it. What it lacked was an entry point, because no
 * chip in #denoms emitted `$custom`.
 *
 * This module supplies the entry point and the value, and owns nothing that
 * pab-mockup.js already owns:
 *
 *   - denomination selection stays in pab-mockup.js. We add a chip whose
 *     data-denom is "$custom"; its generic click handler does the rest.
 *   - the Denomination Change fee stays in pab-mockup.js. We toggle
 *     #denom-change-option and dispatch a change event, so its own listener
 *     recalculates. We never compute a fee here.
 *   - Ultimate-tier inclusion stays in pab-mockup.js (syncDenomChangeOption
 *     force-checks and disables the option). We only read that state.
 *
 * The only state this module holds is the customer's typed value, which
 * nothing else tracks.
 */
(function () {
  'use strict';

  var SENTINEL = '$custom';
  var MAX_VALUE = 999999999;

  var state = { raw: '', normalized: '', valid: false, message: '' };

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function selectedDenom() {
    var el = $('#denoms .selected');
    return el ? String(el.dataset.denom || '') : '';
  }

  function isCustomSelected() { return selectedDenom() === SENTINEL; }

  /* Validation and normalisation — the contract agreed in WP-1.
   * Accepts digits with an optional leading $, optional thousands commas and
   * optional 1–2 decimals. Returns the display form, e.g. "$1,000,000". */
  function normalize(raw) {
    var input = String(raw == null ? '' : raw).trim();

    if (!input) {
      return { valid: false, normalized: '', message: 'Enter the face value you want printed on your bills.' };
    }
    if (!/^\$?[\d,]+(\.\d{0,2})?$/.test(input)) {
      return { valid: false, normalized: '', message: 'Use numbers only — for example 25 or 1,000,000.' };
    }

    var numeric = Number(input.replace(/[$,]/g, ''));
    if (!isFinite(numeric) || numeric <= 0) {
      return { valid: false, normalized: '', message: 'Enter a value greater than zero.' };
    }
    if (numeric > MAX_VALUE) {
      return { valid: false, normalized: '', message: 'Enter a value up to 999,999,999.' };
    }

    var whole = Math.floor(numeric);
    var cents = Math.round((numeric - whole) * 100);
    var grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var display = '$' + grouped + (cents ? '.' + String(cents).padStart(2, '0') : '');

    return { valid: true, normalized: display, message: '' };
  }

  function formatInput(event) {
    var input = document.getElementById('pab-custom-denom-input');
    if (!input) return;
    var raw = input.value;
    var match = /^\$?([\d,]*)(\.\d{0,2})?$/.exec(raw.trim());
    if (!match) return;
    var whole = match[1].replace(/,/g, '');
    var fraction = match[2] || '';
    var addedZero = !whole && !!fraction;
    var formatted = whole || fraction ? '$' + (whole || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',') + fraction : '';
    var start = input.selectionStart;
    var end = input.selectionEnd;
    var direction = input.selectionDirection;

    function position(offset) {
      var count = (raw.slice(0, offset).match(/[\d.]/g) || []).length;
      if (addedZero && count) count += 1;
      var target = formatted ? 1 : 0;
      while (count && target < formatted.length) {
        if (/[\d.]/.test(formatted.charAt(target))) count -= 1;
        target += 1;
      }
      if (event && event.inputType === 'deleteContentForward') {
        while (formatted.charAt(target) === ',') target += 1;
      }
      return target;
    }

    if (formatted !== raw) {
      var nextStart = position(start);
      var nextEnd = position(end);
      input.value = formatted;
      if (start !== null && end !== null) input.setSelectionRange(nextStart, nextEnd, direction);
    }
  }

  function hiddenField() {
    return document.getElementById('billCustomDenominationField');
  }

  function writeHiddenField() {
    var field = hiddenField();
    if (field) field.value = state.valid ? state.normalized : '';
  }

  /* Keep the paid Denomination Change add-on in step with the Custom selection.
   * pab-mockup.js owns the fee; we only drive the checkbox it reads. When the
   * tier is Ultimate the option is force-checked and disabled there, so we
   * leave it alone and avoid any double application. */
  function syncAddOn(custom) {
    var option = document.getElementById('denom-change-option');
    if (!option) return;
    if (option.disabled) return;            // Ultimate — pab-mockup.js owns it
    if (option.checked === custom) return;  // already correct, do not thrash
    option.checked = custom;
    option.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function renderEcho() {
    var text = state.valid ? state.normalized : '';
    $$('[data-pab-custom-denom-echo]').forEach(function (el) {
      el.textContent = text;
      el.hidden = !text;
    });
    $$('[data-pab-custom-denom-notice-value]').forEach(function (el) {
      el.textContent = text || 'Enter above';
    });
    $$('[data-pab-custom-denom-summary]').forEach(function (el) {
      el.hidden = !text;
    });
  }

  function renderError(show) {
    var box = document.getElementById('pab-custom-denom-error');
    var input = document.getElementById('pab-custom-denom-input');
    var visible = !!show && !state.valid && isCustomSelected();
    if (box) {
      box.textContent = visible ? state.message : '';
      box.hidden = !visible;
    }
    if (input) {
      input.setAttribute('aria-invalid', visible ? 'true' : 'false');
      input.classList.toggle('is-invalid', visible);
    }
  }

  function renderVisibility() {
    var custom = isCustomSelected();
    var wrap = document.getElementById('pab-custom-denom-field');
    if (wrap) wrap.hidden = !custom;
    if (!custom) renderError(false);
    renderEcho();
  }

  function readInput(showError) {
    var input = document.getElementById('pab-custom-denom-input');
    state.raw = input ? input.value : '';
    var result = normalize(state.raw);
    state.normalized = result.normalized;
    state.valid = result.valid;
    state.message = result.message;
    writeHiddenField();
    renderEcho();
    renderError(showError);
  }

  /* Called from the denomination chips and on load. */
  function onDenomChanged() {
    var custom = isCustomSelected();
    if (!custom) {
      // Switching back to a standard denomination clears the value and
      // releases the add-on, so the customer is never charged for a
      // denomination change they are no longer making.
      var input = document.getElementById('pab-custom-denom-input');
      if (input) input.value = '';
      state = { raw: '', normalized: '', valid: false, message: '' };
      writeHiddenField();
      syncAddOn(false);
    } else {
      syncAddOn(true);
      readInput(false);
    }
    renderVisibility();
  }

  /* If the customer enables Denomination Change from the Features section
   * without picking Custom, send them to the value field rather than letting
   * them pay for something they were never asked to specify.
   * Unchecking it must also leave Custom, so callout 16 and the Entire bill
   * list row do not stay behind on Standard or Premium. */
  function lastStandardDenomChip() {
    var format = window.PABMockupState && typeof window.PABMockupState.getState === 'function'
      ? window.PABMockupState.getState()
      : null;
    var base = format && String(format.baseBill || '').replace(/^\$/, '');
    var chips = $$('#denoms button');
    var match = chips.filter(function (button) {
      return !button.hidden && String(button.dataset.denom || '') === base;
    })[0];
    if (match) return match;
    return chips.filter(function (button) {
      return !button.hidden && String(button.dataset.denom || '') !== SENTINEL;
    })[0] || null;
  }

  function onAddOnToggled() {
    var option = document.getElementById('denom-change-option');
    if (!option || option.disabled) return;
    if (option.checked && !isCustomSelected()) {
      var chip = Array.prototype.slice.call(document.querySelectorAll('#denoms button')).filter(function (button) {
        return String(button.dataset.denom || '') === SENTINEL;
      })[0];
      if (chip) chip.click();
      return;
    }
    if (!option.checked && isCustomSelected()) {
      var previous = lastStandardDenomChip();
      if (previous) previous.click();
    }
  }

  var api = {
    isCustom: isCustomSelected,
    value: function () { return state.valid ? state.normalized : ''; },
    isValid: function () { return !isCustomSelected() || state.valid; },
    message: function () { return state.message; },
    showError: function () { readInput(true); focusInput(); },
    /* Used by pab-mockup-bridge.js when restoring a saved configuration. */
    restore: function (value) {
      var input = document.getElementById('pab-custom-denom-input');
      if (!input) return;
      var result = normalize(value);
      input.value = result.valid ? result.normalized : (value || '');
      readInput(false);
      renderVisibility();
    }
  };

  function focusInput() {
    var input = document.getElementById('pab-custom-denom-input');
    if (!input) return;
    try { input.focus({ preventScroll: false }); } catch (_e) { input.focus(); }
    input.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  function init() {
    if (!document.getElementById('pab-mockup-root')) return;

    var input = document.getElementById('pab-custom-denom-input');
    if (input) {
      input.addEventListener('input', function (event) {
        if (event.isComposing) return;
        formatInput(event);
        readInput(false);
      });
      input.addEventListener('compositionend', function (event) {
        formatInput(event);
        readInput(false);
      });
      input.addEventListener('blur', function () {
        formatInput();
        readInput(true);
        if (state.valid) input.value = state.normalized;
      });
    }

    // Observe denomination choices from the stable builder root so this keeps
    // working when the responsive UI is rendered after this asset initializes.
    document.getElementById('pab-mockup-root').addEventListener('click', function (event) {
      var button = event.target.closest('#denoms button');
      if (button) window.requestAnimationFrame(onDenomChanged);
    });

    var option = document.getElementById('denom-change-option');
    if (option) {
      option.addEventListener('change', function () {
        window.requestAnimationFrame(onAddOnToggled);
      });
    }

    // Tier changes can flip Ultimate inclusion, which changes whether the
    // add-on is ours to drive.
    $$('#pab-mockup-root .tier').forEach(function (card) {
      card.addEventListener('click', function () {
        window.requestAnimationFrame(function () {
          if (isCustomSelected()) syncAddOn(true);
          renderVisibility();
        });
      });
    });

    renderVisibility();
    writeHiddenField();
  }

  window.PABCustomDenomination = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

